import { useState } from 'react'

import { useLedger, useLedgerSummary } from '@/hooks/useLedger'
import { useProjects } from '@/hooks/useProjects'
import { useCreateLedgerEntry } from '@/hooks/useMutations'
import { Loading } from '@/components/ui/Loading'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { t } from '@/lib/i18n'
import { formatMoney } from '@/lib/format'
import type { LedgerCategory, LedgerDirection } from '@/types/api'
import styles from './LedgerPage.module.css'

const PAGE_SIZE = 20

const CATEGORY_COLORS: Partial<Record<LedgerCategory, string>> = {
  ai_tokens: 'var(--color-accent)',
  infrastructure: 'var(--color-primary)',
  domain_ssl: 'var(--color-violet)',
  saas_subscription: 'var(--color-indigo)',
  payment_fee: 'var(--color-slate)',
  other: '#94a3b8',
}

const CATEGORY_LABELS: Partial<Record<LedgerCategory, string>> = {
  ai_tokens: '🤖 AI 토큰',
  infrastructure: '☁️ 인프라',
  domain_ssl: '🌐 도메인',
  saas_subscription: '📦 SaaS',
  payment_fee: '💳 수수료',
  subscription: '🏷️ 구독',
  one_time_payment: '🏷️ 결제',
  other: '기타',
}

const SOURCE_COLORS: Record<string, string> = {
  stripe: 'var(--color-indigo)',
  cloudflare: 'var(--color-primary)',
  openai: 'var(--color-accent)',
  anthropic: 'var(--color-cost-text)',
  zai: 'var(--color-violet)',
  gandi: 'var(--color-revenue)',
  manual: 'var(--color-slate)',
}

const INBOUND_CATEGORIES: LedgerCategory[] = ['subscription', 'one_time_payment', 'other_income']
const OUTBOUND_CATEGORIES: LedgerCategory[] = ['ai_tokens', 'infrastructure', 'domain_ssl', 'saas_subscription', 'payment_fee', 'other']

const inputStyle: React.CSSProperties = {
  width: '100%', height: 40, padding: '0 12px',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
  fontSize: 14, outline: 'none', background: 'var(--color-surface)', color: 'var(--color-text)',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 16,
}

export default function LedgerPage() {
  const [page, setPage] = useState(1)
  const [direction, setDirection] = useState<string>('')
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({
    direction: 'outbound' as LedgerDirection,
    amountCents: 0,
    category: 'other' as LedgerCategory,
    projectId: '',
    description: '',
  })

  const { data: projects } = useProjects()
  const createEntry = useCreateLedgerEntry()

  const summaryQuery = useLedgerSummary({ period: 'month' })
  const ledgerQuery = useLedger({
    page,
    limit: PAGE_SIZE,
    ...(direction ? { direction } : {}),
  })

  const summary = summaryQuery.data
  const total = ledgerQuery.data?.meta.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    createEntry.mutate(
      {
        direction: form.direction,
        amountCents: form.amountCents,
        category: form.category,
        ...(form.projectId ? { projectId: form.projectId } : {}),
        description: form.description || undefined,
        source: 'manual',
      },
      {
        onSuccess: () => {
          setModalOpen(false)
          setForm({ direction: 'outbound', amountCents: 0, category: 'other', projectId: '', description: '' })
        },
      },
    )
  }

  const availableCategories = form.direction === 'inbound' ? INBOUND_CATEGORIES : OUTBOUND_CATEGORIES

  return (
    <>
      {/* Summary bar */}
      {summaryQuery.isLoading && <Loading variant="block" />}
      {summaryQuery.error && <ErrorState error={summaryQuery.error} onRetry={() => summaryQuery.refetch()} />}
      {summary && (
        <div className={styles.summaryBar}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>수익 (이번 달)</div>
            <div className={`${styles.summaryValue} ${styles.valuePositive}`}>
              {formatMoney(summary.totalInboundCents, { signed: true })}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>비용</div>
            <div className={`${styles.summaryValue} ${styles.valueNegative}`}>
              {formatMoney(summary.totalOutboundCents, { signed: true })}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>순이익</div>
            <div
              className={`${styles.summaryValue} ${summary.netCents >= 0 ? styles.valuePositive : ''}`}
              style={summary.netCents < 0 ? { color: 'var(--color-danger-text)' } : undefined}
            >
              {formatMoney(summary.netCents, { signed: true })}
            </div>
          </div>
        </div>
      )}

      {/* Direction filter + add button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          {[
            { key: '', label: '전체' },
            { key: 'inbound', label: '수익' },
            { key: 'outbound', label: '비용' },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => { setDirection(opt.key); setPage(1) }}
              style={{
                padding: '6px 14px', borderRadius: 'var(--radius-full)',
                border: `1px solid ${direction === opt.key ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: direction === opt.key ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'var(--color-surface)',
                color: direction === opt.key ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                fontSize: 13, fontWeight: direction === opt.key ? 600 : 500,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          style={{
            padding: '8px 16px', background: 'var(--color-primary)', color: '#fff',
            borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13,
          }}
        >
          + {t('Add entry')}
        </button>
      </div>

      {/* Table */}
      <section className={styles.tableSection}>
        {ledgerQuery.isLoading && <Loading variant="rows" count={8} />}
        {ledgerQuery.error && <ErrorState error={ledgerQuery.error} onRetry={() => ledgerQuery.refetch()} />}
        {ledgerQuery.data && ledgerQuery.data.data.length === 0 && (
          <EmptyState title="원장 항목이 없습니다" description="이 기간에 기록된 금전 이동이 없습니다." />
        )}
        {ledgerQuery.data && ledgerQuery.data.data.length > 0 && (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>방향</th>
                    <th>설명</th>
                    <th>카테고리</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerQuery.data.data.map((entry) => {
                    const date = new Date(entry.occurredAt)
                    return (
                      <tr key={entry.id}>
                        <td className={styles.dateCell}>
                          {date.toISOString().slice(5, 10)}
                          <time>{date.toISOString().slice(11, 16)}</time>
                        </td>
                        <td>
                          <span className={`${styles.dirIcon} ${entry.direction === 'inbound' ? styles.dirInbound : styles.dirOutbound}`}>
                            {entry.direction === 'inbound' ? '↘' : '↗'}
                          </span>
                        </td>
                        <td className={styles.descCell}>
                          {entry.description ?? '—'}
                          {entry.source && (
                            <span className={styles.sourceBadge} style={{
                              background: `color-mix(in srgb, ${SOURCE_COLORS[entry.source] ?? 'var(--color-slate)'} 12%, transparent)`,
                              color: SOURCE_COLORS[entry.source] ?? 'var(--color-slate)',
                            }}>
                              {entry.source}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={styles.catBadge}>
                            <span className={styles.catDot} style={{ background: CATEGORY_COLORS[entry.category] ?? '#94a3b8' }} />
                            {CATEGORY_LABELS[entry.category] ?? entry.category}
                          </span>
                        </td>
                        <td className={`${styles.amountCell} ${entry.direction === 'inbound' ? styles.valuePositive : styles.valueNegative}`}>
                          {formatMoney(entry.amountBaseCents, { signed: true })}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles.pagination}>
              <span>총 {total.toLocaleString('ko-KR')}건 중 {Math.min(page * PAGE_SIZE, total)}</span>
              <div className={styles.pageBtns}>
                <button type="button" className={styles.pageBtn} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>‹ 이전</button>
                <span className={styles.pageCurrent}>{page}</span>
                <button type="button" className={styles.pageBtn} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>다음 ›</button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Add entry modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="원장 항목 추가">
        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>방향</label>
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            {(['inbound', 'outbound'] as LedgerDirection[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setForm({ ...form, direction: d, category: (d === 'inbound' ? INBOUND_CATEGORIES : OUTBOUND_CATEGORIES)[0]! })}
                style={{
                  flex: 1, padding: '10px', borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${form.direction === d ? (d === 'inbound' ? 'var(--color-revenue)' : 'var(--color-cost)') : 'var(--color-border)'}`,
                  background: form.direction === d ? (d === 'inbound' ? 'color-mix(in srgb, var(--color-revenue) 10%, transparent)' : 'color-mix(in srgb, var(--color-cost) 10%, transparent)') : 'var(--color-surface)',
                  color: form.direction === d ? (d === 'inbound' ? 'var(--color-revenue-text)' : 'var(--color-cost-text)') : 'var(--color-text-secondary)',
                  fontWeight: 600, fontSize: 14,
                }}
              >
                {d === 'inbound' ? '↘ 수익 (들어옴)' : '↗ 비용 (나감)'}
              </button>
            ))}
          </div>

          <label style={labelStyle} htmlFor="l-amount">금액 (원)</label>
          <input
            id="l-amount"
            style={inputStyle}
            type="number"
            min={1}
            step={1}
            placeholder="예: 50000"
            value={form.amountCents || ''}
            onChange={(e) => setForm({ ...form, amountCents: Number(e.target.value) })}
            required
          />

          <label style={labelStyle} htmlFor="l-category">카테고리</label>
          <select id="l-category" style={inputStyle} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as LedgerCategory })}>
            {availableCategories.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] ?? c}</option>
            ))}
          </select>

          <label style={labelStyle} htmlFor="l-project">프로젝트 (선택)</label>
          <select id="l-project" style={inputStyle} value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
            <option value="">미할당</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <label style={labelStyle} htmlFor="l-desc">설명</label>
          <input
            id="l-desc"
            style={inputStyle}
            type="text"
            placeholder="예: OpenAI GPT-4o 요약 비용"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <button
            type="submit"
            disabled={createEntry.isPending}
            style={{
              marginTop: 24, width: '100%', height: 44,
              background: 'var(--color-primary)', color: '#fff',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
              opacity: createEntry.isPending ? 0.6 : 1,
            }}
          >
            {createEntry.isPending ? '추가 중...' : '항목 추가'}
          </button>
          {createEntry.error && (
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--color-danger-text)' }}>
              {(createEntry.error as Error).message}
            </p>
          )}
        </form>
      </Modal>
    </>
  )
}
